'use client';

import { useEffect, useState, useCallback } from 'react';
import { useAuth } from '@/lib/auth/auth-context';

interface UseCustomLinkCheckOptions {
  checkOnMount?: boolean;
  excludePaths?: string[];
}

export function useCustomLinkCheck(options: UseCustomLinkCheckOptions = {}) {
  const { checkOnMount = true, excludePaths = [] } = options;
  const { user, isLoading: authLoading, isAuthReady } = useAuth();
  const [showCustomLinkDialog, setShowCustomLinkDialog] = useState(false);
  const [isChecking, setIsChecking] = useState(false);

  const checkCustomLink = useCallback(async () => {
    // Don't check if user is not authenticated or is a customer
    if (!user || user.role === 'customer' || authLoading || !isAuthReady) {
      return;
    }

    // Don't check on excluded paths
    const currentPath = window.location.pathname;
    if (excludePaths.some(path => currentPath.startsWith(path))) {
      return;
    }

    setIsChecking(true);
    try {
      const response = await fetch('/api/menu/links');
      if (response.ok) {
        const result = await response.json();
        
        // If no link exists or custom_link_code is empty, show dialog
        if (!result.data || !result.data.custom_link_code) {
          setShowCustomLinkDialog(true);
        }
      }
    } catch (error) {
      console.error('Error checking custom link:', error);
      // Don't show dialog on error to avoid annoying users
    } finally {
      setIsChecking(false);
    }
  }, [user, authLoading, isAuthReady, excludePaths]);

  const handleCustomLinkDialogClose = () => {
    setShowCustomLinkDialog(false);
  };

  // Check on mount if enabled
  useEffect(() => {
    if (checkOnMount && isAuthReady && !authLoading) {
      // Small delay to ensure user data is loaded
      const timeoutId = setTimeout(() => {
        checkCustomLink();
      }, 1000);

      return () => clearTimeout(timeoutId);
    }
  }, [checkOnMount, isAuthReady, authLoading, user, checkCustomLink]);

  return {
    showCustomLinkDialog,
    isChecking,
    checkCustomLink,
    handleCustomLinkDialogClose,
    setShowCustomLinkDialog
  };
}